/**
 * QuestionDetailPanel — presentational right panel of `QuestionsPage`.
 *
 * Composes:
 *   - the mobile back-to-list button (hidden by CSS on wider screens)
 *   - the no-selection placeholder
 *   - the detail header (prev/next nav + slot for detail-actions)
 *   - the detail content: either `renderEditMode` / `renderCreateMode` from
 *     the parent, or the read-only `QuestionDetailView` below.
 *
 * State (selection, editing, navigation) is owned by `QuestionsPage` and
 * passed in via props. Extracted from `QuestionsPage.tsx` during the
 * 2026-05-17 split-pane refactor.
 */
import type { ReactNode } from 'react';
import { LatexText, QuestionFigures } from '../latex';
import { RelatedQuestionsView } from './RelatedQuestionsView';
import { IdControl, ImageViewer, CollapsibleSection } from '../components';
import { QuestionHistoryPanel, type QuestionHistoryApi } from '../question-history';
import { useAppConfig } from '../app-config';
import { ENRICH_STATUS_LABELS } from '../question-set-types';
import { QUESTION_TYPE_LABELS, buildQuestionCorrectAnswerText, type QuestionType } from '@padcanvas/shared';
import type { QuestionDetail } from './types';
import type { ModuleItem } from '../reference-data/types';
import {
    getDifficulty,
    getDifficultyColor,
    getEnrichStatusColor,
    getQuestionTypeColor,
    getReadinessColor,
    getReadinessLabel,
} from './helpers';

export interface QuestionDetailPanelProps {
    selectedQuestionId: string | null;
    selectedQuestion: QuestionDetail | undefined;
    isDetailLoading: boolean;
    isCreating?: boolean;
    isEditing: boolean;
    onBackToList: () => void;

    // Navigation
    currentIndex: number;
    totalCount: number;
    goToPrev: () => void;
    goToNext: () => void;

    // Render slots from the consumer (admin-console / teacher-app)
    renderEditMode?: (
        q: QuestionDetail,
        ctx: { onDone: () => void; modulesGrouped: Record<string, ModuleItem[]> },
    ) => ReactNode;
    renderCreateMode?: (
        ctx: { onDone: () => void; modulesGrouped: Record<string, ModuleItem[]> },
    ) => ReactNode;
    renderDetailActions?: (
        q: QuestionDetail,
        ctx: { refresh: () => void; setEditing: (e: boolean) => void },
    ) => ReactNode;
    renderDetailLeftActions?: () => ReactNode;
    renderDetailBadges?: (q: QuestionDetail) => ReactNode;
    /** Extra content appended to the very end of the read-only detail view. */
    renderDetailExtra?: (q: QuestionDetail) => ReactNode;
    /** Rendered just BEFORE the "More Details" section (e.g. the comments panel). */
    renderBeforeMoreDetails?: (q: QuestionDetail) => ReactNode;
    /** Rendered INSIDE the "More Details" section (e.g. the curricula panel). */
    renderInMoreDetails?: (q: QuestionDetail) => ReactNode;
    /** Resolves a related question's stem for the read-only Related Questions
     *  list. Omitted ⇒ those rows show ids only. */
    fetchQuestionText?: (id: string) => Promise<string>;
    /** Builds the URL a related question opens at (new tab). Omitted ⇒ unlinked. */
    questionHref?: (id: string) => string;
    /** Version-history API — enables the read-only History subsection. */
    historyApi?: QuestionHistoryApi;

    // Edit / view callbacks
    refreshDetail: () => void;
    setIsEditing: (e: boolean) => void;

    // Context for QuestionDetailView and renderEditMode
    isAdmin: boolean;
    /** Drop the internal provenance from More Details — generated keywords, the
     *  import's own source string, and the Cosmos identifiers. For a reader who
     *  OWNS the question but has no use for how it is filed: a student looking
     *  at a question read out of their own paper needs to fix its answer, not to
     *  copy its container id. */
    hideProvenance?: boolean;
    resolveTeacherName?: (ownerId: string) => string | undefined;
    /** The reader. Lets the ownership banner address them directly when the
     *  question is their own, rather than describing them in the third person. */
    viewerId?: string;
    getModuleLabel: (moduleId: string) => string | undefined;
    modulesBySubject: Record<string, ModuleItem[]>;
}

export function QuestionDetailPanel({
    selectedQuestionId,
    selectedQuestion,
    isDetailLoading,
    isCreating,
    isEditing,
    onBackToList,
    currentIndex,
    totalCount,
    goToPrev,
    goToNext,
    renderEditMode,
    renderCreateMode,
    renderDetailActions,
    renderDetailLeftActions,
    renderDetailBadges,
    renderDetailExtra,
    renderBeforeMoreDetails,
    renderInMoreDetails,
    fetchQuestionText,
    questionHref,
    historyApi, viewerId,
    refreshDetail,
    setIsEditing,
    isAdmin,
    hideProvenance,
    resolveTeacherName,
    getModuleLabel,
    modulesBySubject,
}: QuestionDetailPanelProps) {
    return (
        <div className="detail-panel">
            {/* Mobile back-to-list affordance. Hidden via CSS on wider screens —
                wide layout already shows both panels side by side. Clears
                selection (and is ignored in create mode — the create flow's own
                cancel handler owns dismiss). */}
            {selectedQuestionId && !isCreating && (
                <button
                    type="button"
                    className="detail-back-mobile"
                    onClick={onBackToList}
                    aria-label="Back to question list"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                    Back
                </button>
            )}
            {isCreating && renderCreateMode ? (
                <div className="detail-content">
                    {renderCreateMode({
                        onDone: () => {},
                        modulesGrouped: modulesBySubject,
                    })}
                </div>
            ) : !selectedQuestionId ? (
                <div className="no-selection">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <p>Select a question to view details</p>
                </div>
            ) : isDetailLoading || !selectedQuestion ? (
                <div className="no-selection">
                    <div className="loading">Loading question...</div>
                </div>
            ) : (
                <>
                    {/* Detail Header */}
                    <div className="detail-header">
                        {renderDetailLeftActions && !isEditing && (
                            <div className="detail-left-actions">{renderDetailLeftActions()}</div>
                        )}
                        <div className="detail-nav">
                            <button
                                className="btn-nav"
                                onClick={goToPrev}
                                disabled={currentIndex <= 0}
                                title="Previous question"
                            >
                                &larr; Prev
                            </button>
                            <span className="nav-position">{currentIndex + 1} / {totalCount}</span>
                            <button
                                className="btn-nav"
                                onClick={goToNext}
                                disabled={currentIndex >= totalCount - 1}
                                title="Next question"
                            >
                                Next &rarr;
                            </button>
                        </div>
                        {renderDetailActions && !isEditing && (
                            <div className="detail-actions">
                                {renderDetailActions(selectedQuestion, {
                                    refresh: refreshDetail,
                                    setEditing: setIsEditing,
                                })}
                            </div>
                        )}
                    </div>

                    {/* Detail Content */}
                    <div className="detail-content">
                        {isEditing && renderEditMode ? (
                            renderEditMode(selectedQuestion, {
                                onDone: () => setIsEditing(false),
                                modulesGrouped: modulesBySubject,
                            })
                        ) : (
                            <QuestionDetailView
                                hideProvenance={hideProvenance}
                                question={selectedQuestion}
                                getModuleLabel={getModuleLabel}
                                isAdmin={isAdmin}
                                renderBadges={renderDetailBadges}
                                renderExtra={renderDetailExtra}
                                renderBeforeMoreDetails={renderBeforeMoreDetails}
                                renderInMoreDetails={renderInMoreDetails}
                                fetchQuestionText={fetchQuestionText}
                                questionHref={questionHref}
                                resolveTeacherName={resolveTeacherName}
                                viewerId={viewerId}
                                historyApi={historyApi}
                            />
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

/** Read-only detail view for a question. Internal to this panel — not exported
 *  because consumers should compose via the `QuestionsPage` slots. */
function QuestionDetailView({
    question: q,
    getModuleLabel,
    isAdmin,
    hideProvenance,
    renderBadges,
    renderExtra,
    renderBeforeMoreDetails,
    renderInMoreDetails,
    fetchQuestionText,
    questionHref,
    resolveTeacherName,
    historyApi, viewerId,
}: {
    question: QuestionDetail;
    getModuleLabel: (moduleId: string) => string | undefined;
    isAdmin: boolean;
    hideProvenance?: boolean;
    renderBadges?: (question: QuestionDetail) => ReactNode;
    renderExtra?: (question: QuestionDetail) => ReactNode;
    renderBeforeMoreDetails?: (question: QuestionDetail) => ReactNode;
    renderInMoreDetails?: (question: QuestionDetail) => ReactNode;
    fetchQuestionText?: (id: string) => Promise<string>;
    questionHref?: (id: string) => string;
    resolveTeacherName?: (ownerId: string) => string | undefined;
    /** The reader. Lets the ownership banner address them directly when the
     *  question is their own, rather than describing them in the third person. */
    viewerId?: string;
    historyApi?: QuestionHistoryApi;
}) {
    // Custody comes off `ownerId`; `teacherId` is not written or projected any
    // more (Track B, B4). Every owned row carries `ownerId`, old ones included.
    const ownerId = q.ownerId ?? undefined;
    const ownerName = ownerId ? resolveTeacherName?.(ownerId) : undefined;
    // The banner exists to say "this one is restricted, and not yours" while
    // reading a list that also holds library questions. Reading your OWN
    // question it says nothing you do not know — and on My Questions, where
    // every row is yours, it said it on every row, naming the reader in the
    // third person by their own user id.
    const viewerOwnsIt = !!ownerId && ownerId === viewerId;
    const ownerIsStudent = q.ownerType === 'student';
    const { maxHintsForStudent: globalMaxHints } = useAppConfig();
    const maxHintsLabel = q.maxHintsForStudent != null
        ? String(q.maxHintsForStudent)
        : `${globalMaxHints} (default)`;
    return (
        <div className="view-content">
            {ownerId && !viewerOwnsIt && (
                <div className="teacher-question-banner">
                    {ownerIsStudent
                        ? <>A question {ownerName ? <><strong>{ownerName}</strong> </> : null}imported from their own paper.</>
                        : <>This question is available only for teacher{' '}
                            {/* Never a raw id: an unresolved name is a name we do not
                                have, and a uuid tells the reader nothing they can use. */}
                            {ownerName ? <strong>{ownerName}</strong> : 'who created it'} and their students.</>}
                </div>
            )}
            <div className="view-badges">
                {isAdmin && (
                    <span className="badge-readiness" style={{ color: getReadinessColor(q.questionReadiness) }}>
                        {getReadinessLabel(q.questionReadiness)}
                    </span>
                )}
                {q.questionType && (
                    <span className="badge-type" style={{ color: getQuestionTypeColor(q.questionType) }}>
                        {QUESTION_TYPE_LABELS[q.questionType as QuestionType] ?? q.questionType}
                    </span>
                )}
                {/* Admin sees the full draft/live state; a teacher sees only the
                    draft flag (surfacing "live" on every published question would
                    be noise on the public library). */}
                {(isAdmin ? q.importStatus : q.importStatus === 'draft') && (
                    <span className="badge-import-status" style={{ color: q.importStatus === 'draft' ? '#92400e' : '#166534' }}>
                        {q.importStatus}
                    </span>
                )}
                <span className="badge-difficulty" style={{ color: getDifficultyColor(getDifficulty(q)) }}>
                    {getDifficulty(q)}
                </span>
                {q.marks && (
                    <span className="badge-marks">{q.marks} marks</span>
                )}
                {isAdmin && (q.enrichStatus != null && q.enrichStatus > 0) && (
                    <span className="badge-enrich-status" style={{ color: getEnrichStatusColor(q.enrichStatus) }}>
                        {ENRICH_STATUS_LABELS[q.enrichStatus as number] || `Enrich ${q.enrichStatus}`}
                    </span>
                )}
                {renderBadges?.(q)}
            </div>

            <div className="view-section">
                {q.module?.id && getModuleLabel(q.module.id) && (
                    <div style={{ fontSize: 13, color: '#7c3aed', fontWeight: 500, marginBottom: 4 }}>
                        {getModuleLabel(q.module.id)}
                    </div>
                )}
                <h3>Question</h3>
                <div className="question-text">
                    <LatexText text={q.questionText} figures={q.figures} />
                </div>
                {q.questionDiagramUrl && (
                    <ImageViewer src={q.questionDiagramUrl} alt="Question" className="question-image" />
                )}
                <QuestionFigures text={q.questionText} figures={q.figures} className="question-image" />
            </div>

            {/* Correct Answer — skip for MCQ since it's highlighted in options */}
            {!(q.options && q.options.length > 0) && (
                <div className="view-section">
                    <h3>Correct Answer</h3>
                    <div className={`answer-text${q.questionType === 'FREEFORM' ? ' answer-text-multiline' : ''}`}>
                        <LatexText text={buildQuestionCorrectAnswerText(q)} />
                    </div>
                </div>
            )}

            {/* MCQ Options */}
            {q.options && q.options.length > 0 && (
                <div className="view-section">
                    <h3>Options</h3>
                    <div className="options-list">
                        {q.options.map((option, idx) => (
                            <div key={idx} className={`option-item ${option.isCorrect ? 'correct' : ''}`}>
                                <span className="option-label">{option.label.toUpperCase()}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <span className="option-text"><LatexText text={option.text} /></span>
                                    {option.imageUrl && (
                                        <ImageViewer src={option.imageUrl} alt={`Option ${option.label}`}
                                            style={{ display: 'block', maxWidth: '100%', maxHeight: '150px', marginTop: '4px', borderRadius: '4px', border: '1px solid #e5e7eb' }} />
                                    )}
                                </div>
                                {option.isCorrect && <span className="correct-tag">✓</span>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* STRUCTURED Parts — lettered sub-parts with per-part statement,
                answer, and marks. The Question section above is the shared stem. */}
            {q.parts && q.parts.length > 0 && (
                <div className="view-section">
                    <h3>Parts</h3>
                    <div className="parts-list">
                        {q.parts.map((part, idx) => (
                            <div key={part.partId || idx} className="part-item">
                                <div className="part-item-header">
                                    <span className="part-label">Part ({part.partId})</span>
                                    {part.marks != null && <span className="part-marks">{part.marks} mark{part.marks === 1 ? '' : 's'}</span>}
                                </div>
                                {part.statement && (
                                    <div className="part-statement"><LatexText text={part.statement} /></div>
                                )}
                                {part.diagramUrl && (
                                    <ImageViewer src={part.diagramUrl} alt={`Part ${part.partId} diagram`}
                                        style={{ display: 'block', maxWidth: '100%', maxHeight: '160px', marginTop: '4px', borderRadius: '4px', border: '1px solid #e5e7eb' }} />
                                )}
                                {part.correctAnswer && (
                                    <div className="part-answer"><span className="part-answer-label">Answer:</span> <LatexText text={part.correctAnswer} /></div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Serves / Appeared In Exams — primary metadata, kept above the
                solutions rather than buried in More Details.

                This was two sections reading `targetExams` and `class`, which
                QuestionRepository strips before storage — so both were dead on
                every saved question and the panel showed no pool at all. The
                board × grade keys are what carries the fact now. */}
            {q.boardGrades && q.boardGrades.length > 0 && (
                <div className="view-section">
                    <h3>Serves</h3>
                    <div className="target-exams">
                        {q.boardGrades.map(key => (
                            <span key={key} className="target-exam-badge">{key}</span>
                        ))}
                    </div>
                </div>
            )}

            {q.appearedInExams && q.appearedInExams.length > 0 && (
                <div className="view-section">
                    <h3>Appeared In Exams</h3>
                    <div className="exam-list">
                        {q.appearedInExams.map((app, idx) => (
                            <div key={idx} className="exam-item">
                                <span className="exam-name">{app.exam}</span>
                                {app.date && <span className="exam-date">{app.date}</span>}
                                {app.session && <span className="exam-session">{app.session}</span>}
                                {app.questionNo && <span className="exam-qno">{/^\d/.test(app.questionNo) ? `Q.${app.questionNo}` : app.questionNo}</span>}
                                {app.marks && <span className="exam-marks">{app.marks} marks</span>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Solution Image */}
            {q.solutionImageUrl && (
                <div className="view-section">
                    <h3>Solution Image</h3>
                    <ImageViewer src={q.solutionImageUrl} alt="Solution" className="solution-image" />
                </div>
            )}

            {/* Solutions by Style */}
            {q.solutions && q.solutions.length > 0 && (
                <div className="view-section">
                    <h3>Solutions</h3>
                    {q.solutions.map((sol, idx) => {
                        const PROFICIENCY_TO_STYLE: Record<string, string> = { low: 'detailed', medium: 'formal', high: 'quick' };
                        const STYLE_LABELS: Record<string, string> = { detailed: 'Detailed', formal: 'Formal', quick: 'Quick' };
                        const style = sol.style || PROFICIENCY_TO_STYLE[sol.proficiencyLevel ?? ''] || 'formal';
                        const styleLabel = STYLE_LABELS[style] || style;
                        return (
                            <div key={idx} className="solution-view">
                                <div className="solution-header">
                                    <span className={`solution-style-badge ${style}`}>
                                        {styleLabel}
                                    </span>
                                    {sol.label && (
                                        <span className="solution-label-badge">{sol.label}</span>
                                    )}
                                    {sol.targetExams && sol.targetExams.length > 0 && (
                                        <span className="solution-exams-badge">{sol.targetExams.join(', ')}</span>
                                    )}
                                    {sol.expectedTimeSeconds && (
                                        <span className="time-badge">
                                            {sol.expectedTimeSeconds >= 60
                                                ? `${Math.floor(sol.expectedTimeSeconds / 60)}m ${sol.expectedTimeSeconds % 60}s`
                                                : `${sol.expectedTimeSeconds}s`}
                                        </span>
                                    )}
                                </div>
                                <div className="solution-steps">
                                    <LatexText text={sol.steps} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Hints */}
            <div className="view-section">
                <h3>
                    Hints
                    <span style={{ marginLeft: 8, fontSize: 13, color: '#6b7280', fontWeight: 400 }}>
                        Max: {maxHintsLabel}
                    </span>
                </h3>
                {q.hints && q.hints.length > 0 && (
                    <ul className="hints-list">
                        {q.hints.map((hint, idx) => (
                            <li key={idx}><LatexText text={hint} /></li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Comments — shown above More Details so reviewer notes aren't
                buried; the consumer supplies the (read-only + add) panel. */}
            {renderBeforeMoreDetails?.(q)}

            {/* More Details — secondary metadata and provenance, collapsed by
                default so the panel leads with the question content. History is
                a subsection here (read-only; restore lives in edit mode). */}
            <CollapsibleSection title="More Details" defaultExpanded={false} className="question-more-details">
                {/* Curricula (consumer-supplied) */}
                {renderInMoreDetails?.(q)}

                {/* Related questions — read-only. Provenance a reviewer acts on:
                    what this question is related to, and a way to go read it. */}
                <RelatedQuestionsView
                    relatedQuestions={q.relatedQuestions}
                    fetchQuestionText={fetchQuestionText}
                    questionHref={questionHref}
                />

                {/* Keywords */}
                {!hideProvenance && q.keywords && q.keywords.length > 0 && (
                    <div className="view-section">
                        <h3>Keywords</h3>
                        <div className="target-exams">
                            {q.keywords.map(kw => (
                                <span key={kw} className="target-exam-badge">{kw}</span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Question Source */}
                {!hideProvenance && q.questionSource && (
                    <div className="view-section">
                        <h3>Source</h3>
                        <p>{q.questionSource}</p>
                    </div>
                )}

                {/* Identifiers */}
                {!hideProvenance && (
                <div className="view-section">
                    <h3>Identifiers</h3>
                    <div className="identifiers-grid">
                        <div className="identifier-item">
                            <span className="identifier-label">Question ID</span>
                            <IdControl value={q.id} container="questions" />
                        </div>
                        {isAdmin && q.sourceImportId && (
                            <div className="identifier-item">
                                <span className="identifier-label">Source Import ID</span>
                                <IdControl value={q.sourceImportId} container="paperimport-imports" />
                            </div>
                        )}
                        {isAdmin && q.importBatchId && (
                            <div className="identifier-item">
                                <span className="identifier-label">Import Batch ID</span>
                                {/* Not a Cosmos doc — IdControl falls back to copy-only when no container is set. */}
                                <IdControl value={q.importBatchId} />
                            </div>
                        )}
                    </div>
                </div>
                )}

                {/* History — read-only revision trail. Only when a historyApi is
                    wired (admin console); restore stays in the edit form. The
                    panel renders its own "History" toggle, so no wrapping <h3>. */}
                {historyApi && (
                    <div className="view-section">
                        <QuestionHistoryPanel questionId={q.id} historyApi={historyApi} readOnly />
                    </div>
                )}
            </CollapsibleSection>

            {renderExtra?.(q)}
        </div>
    );
}
